import { EVENT_ATTRIBUTES_TYPES } from '@/modules/UIDesign/Events/components/EventRequirements/constants';
import BaseModel from './base';
import User from '@/utils/user';
import { dateFnsFormat } from '@/helpers/dateFns';

export const NUMERIC_TYPES = [
  EVENT_ATTRIBUTES_TYPES.organization,
  EVENT_ATTRIBUTES_TYPES.region,
  EVENT_ATTRIBUTES_TYPES.municipality,
];

export const DISABLED_SYSTEM_ASSIGN = {
  fio: 'fio',
};

// export const SYSTEM_ASSIGNS = [
//   municipality: 'municipality_id',
// ];

/** У аттрибутов есть 3 состояния:
 * 1. Создан на цепочку (конкретный event) |не привязан !!!|
 * 2. Привязан на всё мероприятие [attach / { event_id: 1377, nomination_id: null }]
 * 3. Привязан на конкретную номинацию [attach / { event_id: 1377, nomination_id: 1968 }]
 *
 * @1 можно смотреть только через модель Event / getAttributes({ search[not_assign]: 1 })
 * @2 и @3 можно смотреть через менеджер этой модели
 */
export default class EventAttribute extends BaseModel {
  #user;

  constructor(attribute, url) {
    super(attribute, url);
    this.title = attribute.title;
    this.type = attribute.type;
    this.is_required = attribute.is_required;
    this.is_many_options = attribute.is_many_options;

    this.event_id = attribute.event_id;
    this.nomination_id = attribute.nomination_id || null;

    this.assign = attribute.assign || []; // [{ event_id: 1377, nomination_id: 1958 }, ...]
    this.assign_association = attribute.assign_association;

    /** |На кастомные| String[], ex.: ['Option1', 'Option2']
     * |На системные| Object<{ assign: String, system_attribute: Boolean }>, ex.: { assign: 'snils', system_attribute: true }
     */
    this.options = attribute.options || [];

    this.document = attribute.document || null; // { id, url, original_name }

    this.user_value = this.transformUserValue(attribute.user_value || null); // Ответ пользователя *если через форму запрашивать
    this.user_autocomplete = attribute.user_autocomplete || null; // Сохраненные ответы пользователя *если через форму запрашивать
    if (this.is_many_options && Array.isArray(this.options)) {
      /* eslint-disable-next-line */
      this.user_value = (this.user_value || '').split(/\u001e|␞/);
    }
    this.created_by = attribute.created_by || null;
    this.attribute_value_id = attribute.attribute_value_id || null;

    this.settings = attribute.settings || {};

    this.attributable_id = attribute.attributable_id;
    this.attributable_type = attribute.attributable_type;

    this.owner_id = attribute.owner_id;

    this.#user = new User();
  }

  get required() {
    return this.is_required;
  }

  get isOwner() {
    return [this.settings?.owner_id, this.owner_id]
      .filter(Boolean)
      .includes(this.#user?.data?.id);
  }

  get isAssociation() {
    return this.settings?.association_attribute;
  }

  get isSystem() {
    const { settings } = this;
    return Boolean(settings?.system_attribute || (settings?.global_attribute && !this.#user?.isAdmin));
  }

  get isSystemDisabled() {
    const { settings, isSystem } = this;
    if (!isSystem) return false;
    return Object.values(DISABLED_SYSTEM_ASSIGN).includes(settings?.assign || '');
  }

  get isFio() {
    const { settings, isSystem } = this;
    if (!isSystem) return false;
    return settings?.assign === 'fio';
  }

  get variants() {
    return (this.isSystem ? (this.settings?.variants || this.options?.variants || this.options) : this.options) || [];
  }

  get optionAssign() {
    const assignName = this.settings?.assign;
    return assignName;
  }

  totalAssign(eventId, nominationId) {
    return (!this.event_id || this.event_id === eventId)
      && (this.nomination_id === nominationId || !this.nomination_id);
  }

  currentAssign(eventId, nominationId) {
    return (this.assign || []).find(({ event_id, nomination_id }) =>
      eventId === event_id && nomination_id === (nominationId || null),
    );
  }

  isAssigned(eventId, nominationId) {
    return !!this.currentAssign(eventId, nominationId);
  }

  isGlobal(eventId) {
    return !!this.currentAssign(eventId, null);
  }

  assignRequired(eventId, nominationId) {
    const globalRequired = this.currentAssign(eventId, null);
    return this.currentAssign(eventId, nominationId)?.options?.required
      ?? globalRequired?.options?.required
      ?? this.is_required;
  }

  setValue(val) {
    this.user_value = this.transformUserValue(val);
  }

  transformUserValue(val) {
    if ([null, undefined, ''].includes(val)) return null;
    const { type } = this;
    if (NUMERIC_TYPES.includes(type)) return Number(val);
    if (type === EVENT_ATTRIBUTES_TYPES.birthday) return dateFnsFormat.normalizeDate(val);
    return val;
  }
}
